import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BranchContext = createContext({ branches: [], active: null, setActive: () => {}, refresh: () => {}, isRestricted: false });

const STORAGE_KEY = "radiants_active_branch_id";

export function BranchProvider({ children }) {
  const { user } = useAuth();
  const [allBranches, setAllBranches] = useState([]);
  const [active, _setActive] = useState(null);

  // Restrict the visible branch list to what this user actually owns.
  //   • admin role / no `allowed_branch_ids` → see everything
  //   • otherwise → keep only branches whose id is in user.allowed_branch_ids
  // This drives both the dropdown filter (`<BranchFilter />`) and the
  // implicit default-branch selection on the New voucher forms — a user
  // assigned to Mumbai literally cannot pick Pune from any picker, fully
  // matching the backend's `user_branch_filter` and `assert_branch_access`.
  const allowedIds = useMemo(() => new Set(user?.allowed_branch_ids || []), [user]);
  const isRestricted = !!(user && user.role !== "admin" && allowedIds.size > 0);
  const branches = useMemo(() => {
    if (!isRestricted) return allBranches;
    return allBranches.filter(b => allowedIds.has(b.id));
  }, [allBranches, allowedIds, isRestricted]);

  async function refresh() {
    try {
      const r = await api.get("/branches");
      const list = r.data || [];
      setAllBranches(list);
      // Re-derive `active` against the *filtered* list so a restricted user
      // never lands on a branch they don't own (e.g. one cached in
      // localStorage from a previous role assignment).
      const allowed = (user && user.role !== "admin" && (user.allowed_branch_ids || []).length > 0)
        ? list.filter(b => (user.allowed_branch_ids || []).includes(b.id))
        : list;
      const storedId = localStorage.getItem(STORAGE_KEY);
      let next = allowed.find(b => b.id === storedId);
      if (!next) next = allowed.find(b => b.is_default) || allowed[0] || null;
      _setActive(next || null);
      if (next) localStorage.setItem(STORAGE_KEY, next.id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      setAllBranches([]); _setActive(null);
    }
  }

  function setActive(branchOrId) {
    const id = typeof branchOrId === "string" ? branchOrId : branchOrId?.id;
    const next = branches.find(b => b.id === id) || null;
    _setActive(next);
    if (next) localStorage.setItem(STORAGE_KEY, next.id);
  }

  useEffect(() => {
    if (user) refresh();
    else { setAllBranches([]); _setActive(null); }
  }, [user?.id]);

  return (
    <BranchContext.Provider value={{ branches, active, setActive, refresh, isRestricted }}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() { return useContext(BranchContext); }
